import { useEffect, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'app/components/ui/dialog'
import { Input } from 'app/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { TemplateBodyEditor } from 'app/components/sms-templates/TemplateBodyEditor'
import { pruneFallbacks, TemplateFallbacksEditor } from 'app/components/templates/TemplateFallbacksEditor'
import type { TaskTemplate } from 'app/generated/graphql'
import { toEngineText } from 'app/lib/messageTemplates'
import { readFallbacks, writeFallbacks } from 'app/lib/workflows/messageFallbacks'
import type { TaskTemplateInput } from 'app/stores/TaskTemplateStore'

export type StaffOption = { id: string; name: string }

const NO_OWNER = `__none`

type TaskTemplateEditorDialogProps = {
  open: boolean
  /** Template being edited; undefined = creating a new one. */
  template?: TaskTemplate
  /** Workspace staff for the default-owner select. */
  staffOptions: StaffOption[]
  saving?: boolean
  onSave: (input: TaskTemplateInput) => void | Promise<void>
  onCancel: () => void
}

/** Create/edit dialog for a task template, settings-page authoring only (v1). */
export function TaskTemplateEditorDialog({
  open,
  template,
  staffOptions,
  saving,
  onSave,
  onCancel,
}: TaskTemplateEditorDialogProps) {
  const [name, setName] = useState(``)
  const [title, setTitle] = useState(``)
  const [description, setDescription] = useState(``)
  const [dueInDays, setDueInDays] = useState(``)
  const [ownerId, setOwnerId] = useState(NO_OWNER)
  const [fallbacks, setFallbacks] = useState<Record<string, string>>({})

  // Re-seed on open, keyed on template?.id (not object identity), same
  // rationale as TemplateEditorDialog: store re-parses JSON per fetchList.
  useEffect(() => {
    if (!open) return
    setName(template?.name ?? ``)
    // Fallbacks are stored inside the text; the dialog edits them in their own inputs.
    setTitle(writeFallbacks(template?.title ?? ``, {}))
    setDescription(writeFallbacks(template?.description ?? ``, {}))
    setDueInDays(template?.dueInDays != null ? String(template.dueInDays) : ``)
    setOwnerId(template?.ownerId ?? NO_OWNER)
    setFallbacks(readFallbacks(`${template?.title ?? ``}\n${template?.description ?? ``}`))
  }, [open, template?.id])

  // Honest due validation: empty = no due date; anything else must be a whole
  // number of 1 or more. Garbage disables Save instead of becoming "1 day".
  const dueRaw = dueInDays.trim()
  const parsedDue = dueRaw === `` ? undefined : Number(dueRaw)
  const dueValid = parsedDue === undefined || (Number.isInteger(parsedDue) && parsedDue >= 1)
  const canSave = !!name.trim() && !!title.trim() && dueValid && !saving

  const prunedFallbacks = () => pruneFallbacks(fallbacks, `${title}\n${description}`)

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      {/* max-h + scroll: with fallback rows this dialog can outgrow a phone
          viewport, and the shared DialogContent neither caps nor scrolls, so
          the footer buttons would sit off-screen with no way to reach them. */}
      <DialogContent className="max-h-[90vh] max-w-2xl gap-5 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{template ? `Edit task template` : `New task template`}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="task-template-name" className="text-ui-sm font-medium text-default">
            Name
          </label>
          <Input
            id="task-template-name"
            autoFocus
            value={name}
            placeholder="e.g. Broken appointment follow-up"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="task-template-title" className="text-ui-sm font-medium text-default">
            Task title
          </label>
          <TemplateBodyEditor
            id="task-template-title"
            value={title}
            onChange={setTitle}
            rows={1}
            showSegments={false}
            placeholder={`e.g. Call {{contact.firstName}} about the broken appointment`}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="task-template-description" className="text-ui-sm font-medium text-default">
            Description <span className="font-normal text-minimal">(optional)</span>
          </label>
          <TemplateBodyEditor
            id="task-template-description"
            value={description}
            onChange={setDescription}
            rows={4}
            showSegments={false}
            placeholder="Details the assignee should know…"
          />
        </div>
        {/* Two equal columns from md up, top-aligned so both labels sit on one
            baseline and the suffix never wraps under the input. Below md the
            columns are too narrow for "days after applying" (it collided with
            the owner select at 375px), so they stack. */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:items-start md:gap-6">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="task-template-due" className="text-ui-sm font-medium text-default">
              Due <span className="font-normal text-minimal">(optional)</span>
            </label>
            <div className="flex h-9 items-center gap-2 text-sm text-default">
              <Input
                id="task-template-due"
                type="number"
                min={1}
                inline
                value={dueInDays}
                onChange={(e) => setDueInDays(e.target.value)}
                className="w-24"
              />
              <span className="whitespace-nowrap">days after applying</span>
            </div>
            {!dueValid && <span className="text-xs text-red-500">Enter a whole number of days (1 or more)</span>}
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <label htmlFor="task-template-owner" className="text-ui-sm font-medium text-default">
              Default owner <span className="font-normal text-minimal">(optional)</span>
            </label>
            <Select value={ownerId} onValueChange={setOwnerId}>
              {/* Match the house Input treatment, as the body editors do. */}
              <SelectTrigger id="task-template-owner" className="rounded-lg border-default shadow-elevation-1">
                <SelectValue placeholder="No default owner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_OWNER}>No default owner</SelectItem>
                {staffOptions.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <TemplateFallbacksEditor tokenedText={`${title}\n${description}`} value={fallbacks} onChange={setFallbacks} />
        <DialogFooter>
          <Button variant="white" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            disabled={!canSave}
            onClick={() => {
              const fallbacksToSave = prunedFallbacks()
              // Null, not undefined, so an update clears a field the user emptied.
              onSave({
                name: name.trim(),
                title: toEngineText(title.trim(), fallbacksToSave),
                description: description.trim() ? toEngineText(description.trim(), fallbacksToSave) : null,
                dueInDays: parsedDue ?? null,
                ownerId: ownerId === NO_OWNER ? null : ownerId,
              })
            }}
          >
            {saving ? `Saving…` : `Save template`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
